import {
  exerciseAttribute,
  exerciseField,
} from "../content/lianxi/exercise-fields.js";
import { signedIn } from "./lianxi-gate.js";
import { adopt } from "./lianxi-place-write.js";

/**
 * A signed-in reader's place in an Exercise, put on its public block before
 * the Exercise starts.
 *
 * The block names its Exercise by slug. For a page drawn for somebody signed
 * in, this asks `/user/lianxi/place/` for their place in it, which opens a
 * row the first time, and writes the answer onto the block as the attributes
 * `lianxi-block-data.ts` and `lianxi-yufa-block-data.ts` read: where to post,
 * the drill's stage and pass for each character, or the grammar exercise's
 * queue and missed items with the recordings each item plays
 * (`lianxi-yufa-sounds.ts`). The Exercise then starts from that place and posts
 * every press, with nothing for the reader to do. Until 2026-10-15 the same
 * Exercise was drawn a second time among the reader's own pages for that,
 * behind a "Keep your place" link, and Hugh judged nobody would follow why.
 *
 * **Anything short of a place leaves the block as it was.** A reader with no
 * session, a stale session hint the handler refuses, an Exercise it cannot
 * open and a request that fails all start the Exercise in the browser,
 * keeping nothing, which is what a signed-out reader gets.
 */

/** Where the page asks. `src/user/lianxi-address.ts` names the same path. */
const placePath = "/user/lianxi/place/";

/**
 * Whether a block wants its place asked for before it starts.
 * @param block - The Exercise's block.
 * @returns Whether it names an Exercise and the reader is signed in.
 */
export function wantsPlace(block: HTMLElement): boolean {
  return (block.dataset[exerciseAttribute] ?? "") !== "" && signedIn(block);
}

/**
 * Ask for the reader's place and write it onto the block.
 * @param block - The Exercise's block, naming its slug.
 * @param fetching - How to ask.
 * @returns Once the block carries the place, or is left as it was.
 */
export async function loadPlace(
  block: HTMLElement,
  fetching: typeof fetch,
): Promise<void> {
  const slug = block.dataset[exerciseAttribute] ?? "";
  const query = new URLSearchParams({ [exerciseField]: slug });

  try {
    const response = await fetching(`${placePath}?${query.toString()}`, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });

    if (response.ok) {
      adopt(block, await response.json());
    }
  } catch {
    // The Exercise runs in the browser, keeping nothing
  }
}
